/**
 * E2E renderer probe — mints a real bridge link request (ENG-4209).
 *
 * WHY this exists: the create-studio wizard route
 * (`/agent/:agentId/link/:requestId/studio/create`) cannot be reached by a
 * Playwright test without a LIVE link request: the page's first effect calls
 * `fetchRequestLink(requestId)` and renders `<Problem>` if that fails. Link
 * requests are minted by the local bridge over Electron IPC, not by any
 * user-facing RPC, so a test has no way to produce one from the Node side.
 *
 * This probe calls the SAME function the manual link screen calls
 * (`getLinkAgentIds(force, 'manual')` → `ElectronAPI.sendLinkRequestManual()`),
 * so the ids it returns are the ids the product itself would use. It adds no
 * new transport and no new main-process channel.
 *
 * Gated on `IRIS_E2E_TEST=1` read through the preload bridge, exactly like the
 * sibling probes in this directory (per investigate-before-adding: this is the
 * established convention here, see preset-group-idempotency-probe.ts).
 *
 * Per rules (ai.knowledge/global): code-comments-required,
 * reference-rules-in-comments, investigate-before-adding.
 */
import { getLinkAgentIds } from '#~/agent/components/bridge/hooks.js';
import {
  createRoom,
  listAgents,
  resolveCreateRoomOrganizationId,
  unlinkAgent
} from '#~/api/agent/registry.js';

interface CreateStudioLinkProbe {
  /**
   * Resolves the link ids, or empty strings when they are not available yet.
   * NEVER hangs and NEVER throws: every attempt is bounded, because the
   * sidecar's readiness wait behind `bridge:linkv2` is deliberately unbounded
   * (see studio/electron/src/main/ipc.ts) and a first boot can sit in it for
   * minutes. The caller polls instead.
   */
  mintLinkRequest(timeoutMs?: number): Promise<{ agentId: string; requestId: string }>;

  /**
   * Unlink this launch's bridge, then mint a FRESH, unconsumed link request.
   *
   * WHY: the wizard is only reachable while a link request is still pending.
   * A bridge that boots unlinked mints one and its own auto-link consumes it
   * within a second, after which `fetchRequestLink` answers 404 ("The agent
   * you're trying to link cannot be found"). Unlinking first puts the bridge
   * back in the state a user is in when they link a NEW bridge — which is the
   * only state in which a human ever sees this wizard.
   *
   * Safe in e2e: the bridge belongs to the launch's own userData slot, so this
   * touches that ephemeral agent only.
   */
  unlinkAndMint(token: string, timeoutMs?: number): Promise<{ agentId: string; requestId: string }>;

  /**
   * Call the app's own createRoom twice with ONE idempotency key and report both
   * results. Isolates client from server: the deployed registry answers
   * already_existed=1 on the replay (verified directly over the proto-base64
   * transport), so an alreadyExisted=false here means the flag is lost between
   * the wire and the caller.
   */
  createRoomTwiceWithOneKey(
    token: string,
    name: string,
    key: string,
    secondName?: string
  ): Promise<Array<{ id: string; alreadyExisted: boolean }>>;
}

const EMPTY_IDS = { agentId: '', requestId: '' };

const withBudget = async (
  attempt: Promise<{ agentId: string; requestId: string }>,
  timeoutMs: number
): Promise<{ agentId: string; requestId: string }> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      attempt.catch(() => EMPTY_IDS),
      new Promise<{ agentId: string; requestId: string }>((resolve) => {
        timer = setTimeout(() => resolve(EMPTY_IDS), timeoutMs);
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// Renderer-process gate: read through the preload bridge
// (window.electron.process.env), NOT bare `process.env` — the bundler's define
// replaces `process.env` object reads with `{}` at build time.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const probe: CreateStudioLinkProbe = {
    async mintLinkRequest(timeoutMs = 8_000): Promise<{ agentId: string; requestId: string }> {
      // force=true so a stale cached pair is not returned. The AUTO channel is
      // tried first because that is the one the app itself just used during
      // boot; the MANUAL channel returns no link URL once the local bridge is
      // already linked (observed 2026-08-18: `bridge:link-manual DONE 96ms`
      // with an undefined linkUrl right after a successful auto-link), which
      // would leave the probe with nothing to hand the wizard.
      // FIRST: the ids this renderer already resolved during boot's auto-link.
      // `getLinkAgentIds` memoizes them in module scope, and asking for them
      // without `force` performs no IPC at all. This matters because the
      // sidecar issues a link URL only while the local bridge is UNLINKED —
      // once boot's auto-link consumed one, both IPC channels return undefined
      // and the renderer reports "Linking agent failed" (observed 2026-08-18 on
      // both `bridge:linkv2` and `bridge:link-manual`).
      const cached = await withBudget(getLinkAgentIds(false, 'auto'), timeoutMs);

      if (cached.agentId && cached.requestId) {
        return cached;
      }

      const manual = await withBudget(getLinkAgentIds(true, 'manual'), timeoutMs);

      if (manual.agentId && manual.requestId) {
        return manual;
      }

      return EMPTY_IDS;
    },

    async unlinkAndMint(
      token: string,
      timeoutMs = 15_000
    ): Promise<{ agentId: string; requestId: string; note: string }> {
      const notes: string[] = [];
      const linked = await withBudget(getLinkAgentIds(false, 'auto'), timeoutMs);

      notes.push(linked.agentId ? 'cached-agent' : 'no-cached-agent');

      // When auto-link took its already-linked branch this renderer never
      // resolved a link URL, so there are no cached ids to unlink — but the
      // account's agents are exactly what the harness's own stale-agent sweep
      // unlinks (fixtures/org/stale-agent-sweep.ts). Unlink them all so the
      // sidecar starts issuing link URLs again.
      const agentIds =
        linked.agentId !== ''
          ? [linked.agentId]
          : await listAgents(token).catch((error: unknown) => {
              notes.push(
                `list-agents-failed:${error instanceof Error ? error.message : String(error)}`
              );

              return [] as string[];
            });

      notes.push(`agents:${agentIds.length}`);

      for (const agentId of agentIds) {
        try {
          await unlinkAgent(agentId as never, token);
          notes.push('unlinked');
        } catch (error) {
          // Surface it: a rejected unlink (e.g. an expired token) leaves the
          // bridge linked, and the mint below then returns nothing at all —
          // which reads as "the app never resolved a link request" and hides
          // the real cause.
          notes.push(`unlink-failed:${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const minted = await withBudget(getLinkAgentIds(true, 'manual'), timeoutMs);

      notes.push(minted.requestId ? 'minted' : 'no-link-url');

      return { ...minted, note: notes.join(',') };
    },

    async createRoomTwiceWithOneKey(
      token: string,
      name: string,
      key: string,
      secondName?: string
    ): Promise<Array<{ id: string; alreadyExisted: boolean }>> {
      // ENG-4714: resolve the organization ONCE and pin both calls to it.
      // Server dedup is keyed on (organization_id, idempotency_key), so the
      // assertion this probe feeds is only meaningful within one organization.
      // Leaving organizationId undefined makes createRoom re-resolve it per
      // call, and a sibling spec provisioning an org on the shared e2e account
      // between the two calls moved the target — two organizations, one key, no
      // collision, two Studios. Real product code already pins the org (the
      // create-studio wizard passes selectedOrgId); the probe must too.
      const organizationId = await resolveCreateRoomOrganizationId(token, name);

      const first = await createRoom(name, token, organizationId, key);
      // A DIFFERENT name on the replay separates key-dedup from the legacy
      // name-uniqueness dedup: only a key-aware responder returns the first room.
      const second = await createRoom(secondName ?? name, token, organizationId, key);

      return [
        { id: String(first.id), alreadyExisted: first.alreadyExisted },
        { id: String(second.id), alreadyExisted: second.alreadyExisted }
      ];
    }
  };

  (
    globalThis as { __irisCreateStudioLinkProbe?: CreateStudioLinkProbe }
  ).__irisCreateStudioLinkProbe = probe;
}
